/**
 * @typedef {{ epi?: string | number }} SwymWishlistItem
 * @typedef {{ resource?: { id?: string | number; available?: boolean; featured_media?: { preview_image?: { src?: string } }; featured_image?: { src?: string }; title?: string; price?: number }; data?: { productId?: string; newProduct?: { id: string; url: string }; sourceElement?: Element | null } }} VariantUpdateDetail
 * @typedef {Window & { _swat?: unknown }} WindowWithSwat
 */

const SWYM_ATW_DEBUG = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('swymAtwDebug') === '1';

/** @typedef {Window & { _swat?: { ui?: { performAddToWishlist?: (data: object, onSuccess?: () => void, onError?: (err: unknown) => void) => void }; addToWishList?: (data: object, cb?: () => void) => void; fetch?: (cb: (wishlist: SwymWishlistItem[]) => void) => void; currentPageData?: Record<string, unknown> }; swymATWButtonConfig?: { ATWButtonTextColor?: string; fontSize?: string; fontFamily?: string; fontWeight?: string; letterSpacing?: string; borderRadius?: string } }} SwatWindow */

/** Star path + SVG attributes from theme (snippet swym-atw-star-path sets window.SWYM_ATW_STAR_PATH) so icon/styling stay theme-controlled. */
const SWYM_ATW_STAR_PATH =
  (typeof window !== 'undefined' && /** @type {Window & { SWYM_ATW_STAR_PATH?: Record<string, string> }} */ (window).SWYM_ATW_STAR_PATH) || null;

/**
 * @param {HTMLElement} button
 * @param {HTMLElement | null} container
 */
function ensureSwymPdpButtonActivated(button, container) {
  const icon = button.querySelector('#swym-atw-pdp-button-icon');
  if (icon && !icon.querySelector('path') && SWYM_ATW_STAR_PATH?.d) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    for (const [key, value] of Object.entries(SWYM_ATW_STAR_PATH)) {
      if (value != null && value !== '') path.setAttribute(key, String(value));
    }
    icon.appendChild(path);
  }
  if (container instanceof HTMLElement) {
    if (!container.classList.contains('product_title_right-position')) container.classList.add('product_title_right-position');
    if (!container.classList.contains('consent-given')) container.classList.add('consent-given');
  }
  const win = /** @type {Window & { swymATWButtonConfig?: Record<string, unknown> }} */ (window);
  const config = win.swymATWButtonConfig;
  if (config && button instanceof HTMLElement) {
    const s = button.style;
    if (typeof config.ATWButtonTextColor === 'string') s.color = config.ATWButtonTextColor;
    if (typeof config.fontSize === 'string') s.fontSize = config.fontSize;
    if (typeof config.fontFamily === 'string') s.fontFamily = config.fontFamily;
    if (typeof config.fontWeight === 'string') s.fontWeight = config.fontWeight;
    else if (config.fontWeight == null) s.fontWeight = '400';
    if (typeof config.letterSpacing === 'string') s.letterSpacing = config.letterSpacing;
    if (config.borderRadius !== undefined) s.borderRadius = String(config.borderRadius);
  }
}

/**
 * @param {HTMLElement} button
 * @param {{ id?: string | number }} variant
 * @param {NonNullable<SwatWindow['_swat']>} swat
 * @param {{ skipReplace?: boolean }} [options]
 */
function applyVariantToPdpButton(button, variant, swat, options = {}) {
  const container = button.closest('#swym-atw-button-container');
  if (container instanceof HTMLElement) container.style.removeProperty('display');
  if (button instanceof HTMLElement) button.style.removeProperty('display');

  const skipReplace = options.skipReplace === true;
  let targetButton = button;

  if (!skipReplace) {
    /** Clone and replace so the new node gets the click listener; clone keeps data-swym-theme-bound and would not re-bind. */
    const freshButton = /** @type {HTMLElement} */ (button.cloneNode(true));
    freshButton.classList.remove('atw-button-loading');
    delete freshButton.dataset.swymThemeBound;
    const loaderWrapper = freshButton.querySelector('.swym-atw-animated-loader-wrapper');
    if (loaderWrapper instanceof HTMLElement) loaderWrapper.classList.add('swym-atw-loader-hidden');
    const parent = button.parentNode;
    if (!parent) return;
    parent.replaceChild(freshButton, button);
    targetButton = freshButton;
  } else {
    targetButton.classList.remove('atw-button-loading');
    const loaderWrapper = targetButton.querySelector('.swym-atw-animated-loader-wrapper');
    if (loaderWrapper instanceof HTMLElement) loaderWrapper.classList.add('swym-atw-loader-hidden');
  }

  if (!targetButton.dataset.swymThemeBound) {
    targetButton.dataset.swymThemeBound = 'true';
    targetButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const cpd = swat.currentPageData;
      if (!cpd) return;
      const cont = targetButton.closest('#swym-atw-button-container');
      const contEl = cont instanceof HTMLElement ? cont : null;
      /** After combined-listing morph the button lives in .swym-atw-btn-wrapper; we store product/variant/price etc. on the container so the modal gets the current product, not the one from initial page. */
      const useStored = contEl?.dataset.swymThemeMoved === 'true';
      const epiOverride = useStored ? (contEl?.dataset.variantId ?? null) : null;
      const empiOverride = useStored ? (contEl?.dataset.productId ?? null) : null;
      const productData = {
        epi: epiOverride ?? cpd.epi,
        empi: empiOverride ?? cpd.empi,
        du: useStored && contEl?.dataset.productUrl ? contEl.dataset.productUrl : (cpd.du || window.location.href),
        dt: useStored && contEl?.dataset.productTitle ? contEl.dataset.productTitle : cpd.dt,
        iu: useStored && contEl?.dataset.imageUrl ? contEl.dataset.imageUrl : cpd.iu,
        pr: useStored && contEl?.dataset.price ? Number(contEl.dataset.price) / 100 : cpd.pr,
        vi: useStored && contEl?.dataset.variantTitle ? contEl.dataset.variantTitle : cpd.vi,
      };
      if (useStored && swat.currentPageData) {
        swat.currentPageData.epi = productData.epi;
        swat.currentPageData.empi = productData.empi;
        swat.currentPageData.du = productData.du;
        swat.currentPageData.dt = productData.dt;
        swat.currentPageData.iu = productData.iu;
        swat.currentPageData.pr = productData.pr;
        swat.currentPageData.vi = productData.vi;
      }
      const syncButtonState = () => {
        targetButton.classList.toggle('swym-added');
        targetButton.classList.toggle('atw-button-added');
        updatePdpButtonIcon(targetButton, targetButton.classList.contains('swym-added'));
      };
      if (swat.ui?.performAddToWishlist) {
        swat.ui.performAddToWishlist(
          productData,
          syncButtonState,
          (/** @type {unknown} */ err) => console.error('Swym ATW error', err)
        );
      } else if (swat.addToWishList) {
        swat.addToWishList(productData, syncButtonState);
      }
    });
  }

  /** Reflect wishlist state for this variant so the star shows filled when already in list. */
  swat.fetch?.((/** @type {SwymWishlistItem[]} */ wishlist) => {
    const isInWishlist = wishlist.some(
      (/** @type {SwymWishlistItem} */ item) => String(item.epi) === String(variant.id)
    );
    targetButton.classList.toggle('swym-added', isInWishlist);
    targetButton.classList.toggle('atw-button-added', isInWishlist);
    updatePdpButtonIcon(targetButton, isInWishlist);
  });

  if (skipReplace && container instanceof HTMLElement) {
    container.dataset.variantId = String(variant.id);
    if (!container.dataset.productId) {
      const productId = swat.currentPageData?.empi;
      if (productId != null) container.dataset.productId = String(productId);
    }
    ensureSwymPdpButtonActivated(targetButton, container);
  }
}

let swymPdpAppliedOnce = false;

/** PDP wishlist star icon path (same shape for empty and filled). */
const PDP_STAR_PATH_D =
  'M11.2691 4.41115C11.5006 3.89177 11.6164 3.63208 11.7776 3.55211C11.9176 3.48263 12.082 3.48263 12.222 3.55211C12.3832 3.63208 12.499 3.89177 12.7305 4.41115L14.5745 8.54808C14.643 8.70162 14.6772 8.77839 14.7302 8.83718C14.777 8.8892 14.8343 8.93081 14.8982 8.95929C14.9705 8.99149 15.0541 9.00031 15.2213 9.01795L19.7256 9.49336C20.2911 9.55304 20.5738 9.58288 20.6997 9.71147C20.809 9.82316 20.8598 9.97956 20.837 10.1342C20.8108 10.3122 20.5996 10.5025 20.1772 10.8832L16.8125 13.9154C16.6877 14.0279 16.6252 14.0842 16.5857 14.1527C16.5507 14.2134 16.5288 14.2807 16.5215 14.3503C16.5132 14.429 16.5306 14.5112 16.5655 14.6757L17.5053 19.1064C17.6233 19.6627 17.6823 19.9408 17.5989 20.1002C17.5264 20.2388 17.3934 20.3354 17.2393 20.3615C17.0619 20.3915 16.8156 20.2495 16.323 19.9654L12.3995 17.7024C12.2539 17.6184 12.1811 17.5765 12.1037 17.56C12.0352 17.5455 11.9644 17.5455 11.8959 17.56C11.8185 17.5765 11.7457 17.6184 11.6001 17.7024L7.67662 19.9654C7.18404 20.2495 6.93775 20.3915 6.76034 20.3615C6.60623 20.3354 6.47319 20.2388 6.40075 20.1002C6.31736 19.9408 6.37635 19.6627 6.49434 19.1064L7.4341 14.6757C7.46898 14.5112 7.48642 14.429 7.47814 14.3503C7.47081 14.2807 7.44894 14.2134 7.41394 14.1527C7.37439 14.0842 7.31195 14.0279 7.18708 13.9154L3.82246 10.8832C3.40005 10.5025 3.18884 10.3122 3.16258 10.1342C3.13978 9.97956 3.19059 9.82316 3.29993 9.71147C3.42581 9.58288 3.70856 9.55304 4.27406 9.49336L8.77835 9.01795C8.94553 9.00031 9.02911 8.99149 9.10139 8.95929C9.16534 8.93081 9.2226 8.8892 9.26946 8.83718C9.32241 8.77839 9.35663 8.70162 9.42508 8.54808L11.2691 4.41115Z';

/** Empty star SVG path (fill=none, stroke outline only). Color #32312f per design. */
const PDP_STAR_SVG_EMPTY = `<path d="${PDP_STAR_PATH_D}" stroke="#32312f" stroke-width="1px" fill="none" stroke-linecap="round" stroke-linejoin="round"></path>`;
/** Filled star SVG path. Color #32312f per design (not currentColor). */
const PDP_STAR_SVG_FILLED = `<path d="${PDP_STAR_PATH_D}" stroke="#32312f" stroke-width="1px" fill="#32312f" stroke-linecap="round" stroke-linejoin="round"></path>`;

/**
 * Replace PDP wishlist button icon with empty or filled star SVG.
 * Swym app block injects the button; we swap the path innerHTML for reliable visual state.
 * @param {HTMLElement} btn
 * @param {boolean} isAdded
 */
function updatePdpButtonIcon(/** @type {HTMLElement} */ btn, /** @type {boolean} */ isAdded) {
  const icon = btn.querySelector('#swym-atw-pdp-button-icon');
  if (icon) {
    icon.innerHTML = isAdded ? PDP_STAR_SVG_FILLED : PDP_STAR_SVG_EMPTY;
  }
}

/**
 * Listen to Swym wishlist events and sync PDP button icon.
 * Swym app block event semantics differ from docs: addedToWishlist fires on remove, removedFromWishlist on add.
 * Verified in production—inverted mapping required for correct star display.
 * @param {NonNullable<SwatWindow['_swat']>} swat
 */
function setupPdpWishlistEventListeners(swat) {
  const swatAny = /** @type {{ evtLayer?: { addEventListener?: (event: string, cb: () => void) => void }; JSEvents?: { addedToWishlist?: string; removedFromWishlist?: string } }} */ (swat);
  const evtLayer = swatAny.evtLayer;
  const JSEvents = swatAny.JSEvents;
  if (!evtLayer?.addEventListener || !JSEvents?.addedToWishlist || !JSEvents?.removedFromWishlist) return;
  const setAdded = (/** @type {boolean} */ isAdded) => {
    const btn = document.querySelector('#swym-atw-pdp-button');
    if (btn instanceof HTMLElement) {
      btn.classList.toggle('swym-added', isAdded);
      btn.classList.toggle('atw-button-added', isAdded);
      updatePdpButtonIcon(btn, isAdded);
    }
  };
  evtLayer.addEventListener(JSEvents.addedToWishlist, () => setAdded(false));
  evtLayer.addEventListener(JSEvents.removedFromWishlist, () => setAdded(true));
}

/** Swym injects the button inside .shopify-app-block; we move it next to the product title so it stays visible and in a stable place after main is morphed on combined-listing switch. */
function moveSwymPdpButtonToTitleArea() {
  const btnContainer = document.querySelector('#swym-atw-button-container');
  if (!(btnContainer instanceof HTMLElement) || !btnContainer.closest('.shopify-app-block')) return false;

  const existingWrapper = document.querySelector('.swym-atw-btn-wrapper');
  if (existingWrapper?.contains(btnContainer)) return true;

  const titleEl =
    document.querySelector('.product-details--main product-title') ||
    document.querySelector('.product-details--main h1') ||
    document.querySelector('[data-product-grid-content] .product-details--main h1');
  if (!(titleEl instanceof HTMLElement) || !titleEl.parentNode) return false;

  const wrapper = document.createElement('div');
  wrapper.className = 'swym-atw-btn-wrapper product_title_right-position';
  wrapper.id = 'swym-embed-pdp-btn-wrapper';

  titleEl.parentNode.insertBefore(wrapper, titleEl);
  wrapper.appendChild(btnContainer);
  wrapper.appendChild(titleEl);
  return true;
}

/** Fired by variant-picker on variant change or combined-listing product switch; we update Swym’s currentPageData, apply to the PDP button, and when main was morphed we re-run move + apply so the button stays in the title area with correct product/variant. */
/** PDP product id from main variant-picker or initial script; used to ignore variant:update from quick-add/modal (different product). */
function getPdpProductId() {
  const mainVp = document.querySelector('main variant-picker');
  if (mainVp instanceof HTMLElement && mainVp.dataset.productId) return mainVp.dataset.productId;
  const script = document.querySelector('#swym-initial-variant');
  if (!script?.textContent) return '';
  try {
    const parsed = JSON.parse(script.textContent);
    return String(parsed?.productId ?? '');
  } catch {
    return '';
  }
}

document.addEventListener('variant:update', (/** @type {Event & { detail?: VariantUpdateDetail }} */ event) => {
  const detail = event.detail;
  const variant = detail?.resource;
  const data = detail?.data;
  const swat = /** @type {SwatWindow} */ (window)._swat;
  const button = document.querySelector('#swym-atw-pdp-button');

  /** Ignore events from other products (e.g. quick-add modal); only process our initial dispatch or when productId matches the PDP. */
  const pdpProductId = getPdpProductId();
  if (data?.sourceElement != null && pdpProductId && data?.productId !== pdpProductId) return;

  const isCombinedListingSwitch = !!data?.newProduct;
  if (SWYM_ATW_DEBUG) {
    console.log('[Swym ATW] variant:update', {
      isCombinedListingSwitch,
      productId: data?.productId,
      newProduct: data?.newProduct,
      variantId: variant?.id,
      hasSwat: !!swat,
      hasButton: !!button,
      buttonParentId: button?.parentElement?.id ?? null,
      buttonInAppBlock: button ? !!button.closest('.shopify-app-block') : null,
    });
  }

  if (!variant?.id || !swat) return;

  const variantImage =
    variant.featured_media?.preview_image?.src ??
    variant.featured_image?.src ??
    swat.currentPageData?.iu;

  if (swat.currentPageData) {
    swat.currentPageData.epi = variant.id;
    swat.currentPageData.available = variant.available ?? true;
    swat.currentPageData.vi = variant.title ?? swat.currentPageData.vi;
    swat.currentPageData.pr =
      variant.price != null ? variant.price / 100 : swat.currentPageData.pr;
    swat.currentPageData.uri = window.location.href;
    if (variantImage) swat.currentPageData.iu = variantImage;
    const newProduct = event.detail?.data?.newProduct;
    const productId = event.detail?.data?.productId;
    if (newProduct?.id) {
      swat.currentPageData.empi = newProduct.id;
    } else if (productId && swat.currentPageData.empi == null) {
      swat.currentPageData.empi = productId;
    }
  }

  let buttonEl = document.querySelector('#swym-atw-pdp-button');
  if (!(buttonEl instanceof HTMLElement)) {
    if (data?.sourceElement == null && document.querySelector('#swym-initial-variant') && !pdpInitialApplyRetryScheduled) {
      pdpInitialApplyRetryScheduled = true;
      schedulePdpApplyWhenButtonAppears();
    }
    return;
  }
  /** After first apply, combined-listing switch morphs <main> so the button is still inside app block; we move it again and apply with stored data (skipReplace) so we don’t clone/replace and we use container dataset for modal. */
  const needMove = buttonEl.closest('.shopify-app-block') && swymPdpAppliedOnce;
  if (needMove) {
    if (data?.productId && swat.currentPageData) {
      swat.currentPageData.empi = data.productId;
    }
    const containerForProductId = buttonEl.closest('#swym-atw-button-container');
    if (containerForProductId instanceof HTMLElement && data?.productId) {
      containerForProductId.dataset.productId = data.productId;
    }
    /** Wait for morph to finish so we query the live DOM for variant-picker and title. */
    const runDeferredApply = () => {
      moveSwymPdpButtonToTitleArea();
      const btn = document.querySelector('#swym-atw-pdp-button');
      if (btn instanceof HTMLElement) {
        const cont = btn.closest('#swym-atw-button-container');
        if (cont instanceof HTMLElement) {
          const vp = document.querySelector('variant-picker');
          const productIdFromPage = vp instanceof HTMLElement ? vp.dataset.productId : undefined;
          if (productIdFromPage) cont.dataset.productId = productIdFromPage;
          cont.dataset.variantId = String(variant.id);
          cont.dataset.variantTitle = variant.title ?? '';
          cont.dataset.price = String(variant.price ?? 0);
          cont.dataset.imageUrl =
            variant.featured_media?.preview_image?.src ??
            variant.featured_image?.src ??
            '';
          cont.dataset.productUrl = window.location.href;
          const wrapper = cont.closest('.swym-atw-btn-wrapper');
          const productTitleEl = wrapper instanceof HTMLElement ? wrapper.querySelector('h1') : null;
          cont.dataset.productTitle = productTitleEl?.textContent?.trim() ?? '';
          cont.dataset.swymThemeMoved = 'true';
        }
        applyVariantToPdpButton(btn, variant, swat, { skipReplace: true });
        if (SWYM_ATW_DEBUG) console.log('[Swym ATW] applied variant to button (deferred)');
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(runDeferredApply));
  } else {
    const containerNormal = buttonEl.closest('#swym-atw-button-container');
    if (containerNormal instanceof HTMLElement) {
      delete containerNormal.dataset.swymThemeMoved;
    }
    applyVariantToPdpButton(buttonEl, variant, swat);
  }
  swymPdpAppliedOnce = true;
  if (SWYM_ATW_DEBUG && !needMove) console.log('[Swym ATW] applied variant to button');
});

/** @param {string | number} variantId */
function syncPdpUrlVariant(variantId) {
  const url = new URL(window.location.href);
  if (!variantId || url.searchParams.get('variant') === String(variantId)) return;
  url.searchParams.set('variant', String(variantId));
  history.replaceState({}, '', url.toString());
}

let pdpInitialVariantDispatched = false;
let pdpInitialApplyRetryScheduled = false;

function resetPdpSwymState() {
  pdpInitialVariantDispatched = false;
  pdpInitialApplyRetryScheduled = false;
  swymPdpAppliedOnce = false;
}

/** If the button is injected by Swym after our initial dispatch, poll and re-dispatch once so apply runs on first render. */
function schedulePdpApplyWhenButtonAppears() {
  const maxTries = 50;
  let tries = 0;
  const id = setInterval(() => {
    tries++;
    const btn = document.querySelector('#swym-atw-pdp-button');
    if (btn instanceof HTMLElement && /** @type {WindowWithSwat} */ (window)._swat) {
      clearInterval(id);
      const script = document.querySelector('#swym-initial-variant');
      if (!script?.textContent) return;
      try {
        const parsed = JSON.parse(script.textContent);
        if (!parsed?.id) return;
        const resource = {
          id: parsed.id,
          available: parsed.available,
          title: parsed.title,
          price: parsed.price,
          featured_media: parsed.featured_media,
          featured_image: parsed.featured_image,
        };
        document.dispatchEvent(
          new CustomEvent('variant:update', {
            detail: {
              resource,
              sourceId: '',
              data: {
                html: null,
                productId: String(parsed.productId ?? ''),
                newProduct: undefined,
                sourceElement: null,
              },
            },
            bubbles: true,
          })
        );
      } catch (_) {}
    } else if (tries >= maxTries) clearInterval(id);
  }, 100);
}

/** Emit variant:update from #swym-initial-variant JSON so Swym button and URL get the initial variant without waiting for picker interaction. */
function dispatchPdpInitialVariant() {
  if (pdpInitialVariantDispatched) return;
  const script = document.querySelector('#swym-initial-variant');
  if (!script?.textContent) return;
  try {
    const parsed = JSON.parse(script.textContent);
    if (!parsed?.id) return;
    syncPdpUrlVariant(parsed.id);
    const resource = {
      id: parsed.id,
      available: parsed.available,
      title: parsed.title,
      price: parsed.price,
      featured_media: parsed.featured_media,
      featured_image: parsed.featured_image,
    };
    pdpInitialVariantDispatched = true;
    document.dispatchEvent(
      new CustomEvent('variant:update', {
        detail: {
          resource,
          sourceId: '',
          data: {
            html: null,
            productId: String(parsed.productId ?? ''),
            newProduct: undefined,
            sourceElement: null,
          },
        },
        bubbles: true,
      })
    );
  } catch (_) {}
}

function runPdpInitialVariantWhenReady() {
  requestAnimationFrame(() => {
    dispatchPdpInitialVariant();
  });
}

function schedulePdpInitialVariantOnLoad() {
  const win = /** @type {WindowWithSwat} */ (window);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (win._swat) runPdpInitialVariantWhenReady();
    });
  } else if (win._swat) {
    runPdpInitialVariantWhenReady();
  }
}

schedulePdpInitialVariantOnLoad();

const hasPdpInitialVariant = document.querySelector('#swym-initial-variant');
if (hasPdpInitialVariant) {
  const win = /** @type {WindowWithSwat} */ (window);
  /** Swym loads async; if _swat is set later we need to run initial variant once it’s available. */
  const existing = Object.getOwnPropertyDescriptor(window, '_swat');
  const setupWhenSwatReady = (/** @type {NonNullable<SwatWindow['_swat']>} */ swat) => {
    runPdpInitialVariantWhenReady();
    setupPdpWishlistEventListeners(swat);
    document.dispatchEvent(new CustomEvent('swym:ready', { bubbles: true }));
  };
  if (!existing?.set) {
    let _swatValue = win._swat;
    Object.defineProperty(window, '_swat', {
      get() {
        return _swatValue;
      },
      set(v) {
        _swatValue = v;
        if (v) setupWhenSwatReady(v);
      },
      configurable: true,
      enumerable: true,
    });
    if (_swatValue) setupWhenSwatReady(_swatValue);
  } else {
    if (win._swat) setupWhenSwatReady(win._swat);
  }

  window.addEventListener('pagereveal', () => {
    resetPdpSwymState();
    if (document.querySelector('#swym-initial-variant') && win._swat) runPdpInitialVariantWhenReady();
  }, { once: false });
}
